import { useCallback } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceScopeId } from '@/hooks/useWorkspaceScopeId';
import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';
import type {
  BusinessProfileSettings,
  UpdateBusinessProfileResult,
} from '@/lib/commerce-v2/storefrontSupport';
import type { OrderStatus } from '@/lib/api/services/orderStatuses';
import type { CatalogPublishState } from '@/lib/commerce-v2/catalogPublishState';
import { loadOrderStatuses } from '@/lib/commerce/orderStatusList';

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  category?: string;
  custom_label_0?: string;
  image_url?: string;
  /** Meta's own (fbcdn) copy of the image — loads when the store's original link is dead. */
  meta_image_url?: string | null;
  url?: string;
  status: 'active' | 'inactive';
  whatsapp_product_id?: string;
  retailer_id?: string; // This is the SKU/content_id needed for product messages
  created_at: string;
  updated_at: string;
}

export interface CatalogInfo {
  id: string;
  name: string;
  business_account_id: string;
  whatsapp_catalog_id: string;
  status: 'connected' | 'disconnected' | 'syncing';
  last_sync_at?: string;
  product_count: number;
  created_at: string;
  updated_at: string;
}

export interface AvailableCatalog {
  id: string;
  name: string;
  product_count: number;
  status: string;
  whatsapp_catalog_id?: string;
}

export interface CommerceStats {
  total_revenue: number;
  total_orders: number;
  total_products: number;
  avg_order_value: number;
  revenue_change: number;
  orders_change: number;
  products_change: number;
  aov_change: number;
  top_products: Array<{
    productName: string;
    totalSold: number;
    totalRevenue: number;
  }>;
  sales_history: Array<{
    name: string;
    date: string;
    sales: number;
    orders: number;
  }>;
  sources?: {
    native: { revenue: number; orders: number; products: number };
    shopify: { revenue: number; orders: number; products: number };
    woocommerce: { revenue: number; orders: number; products: number };
  };
}

export interface Order {
  id: string;
  order_number: string;
  source: 'native' | 'shopify' | 'woocommerce';
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  total_amount: number;
  currency: string;
  status: string;
  payment_status: string;
  created_at: string;
  items_count: number;
  source_id?: string;
  invoice_id?: string | null;
  invoice_number?: string | null;
  invoice_status?: string | null;
}

export const ORDERS_PAGE_KEY = ['commerce-v2', 'orders-page'] as const;
export const ORDERS_FACETS_KEY = ['commerce-v2', 'orders-facets'] as const;

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (params: { orderId: string; status: string; syncPetpooja?: boolean; meta_data?: Record<string, any> }) => {
      if (!workspaceScopeId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/orders/${params.orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: workspaceScopeId,
          status: params.status,
          syncPetpooja: params.syncPetpooja ?? true,
          ...(params.meta_data ? { meta_data: params.meta_data } : {}),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to update order');
      }
      return response.json();
    },
    onMutate: async ({ orderId, status }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['orders'] }),
        queryClient.cancelQueries({ queryKey: ORDERS_PAGE_KEY }),
      ]);

      const previous = [
        ...queryClient.getQueriesData({ queryKey: ['orders'] }),
        ...queryClient.getQueriesData({ queryKey: ORDERS_PAGE_KEY }),
      ];

      queryClient.setQueriesData({ queryKey: ['orders'] }, (data: unknown) => {
        const bag = data as { orders?: unknown } | undefined;
        if (!bag || !Array.isArray(bag.orders)) return data;
        let hit = false;
        const orders = (bag.orders as Record<string, unknown>[]).map((o) => {
          if (String(o?.id ?? '') !== String(orderId)) return o;
          hit = true;
          return { ...o, status };
        });
        return hit ? { ...bag, orders } : data;
      });

      queryClient.setQueriesData({ queryKey: ORDERS_PAGE_KEY }, (data: unknown) => {
        const page = data as { rows?: unknown } | undefined;
        if (!page || !Array.isArray(page.rows)) return data;
        let hit = false;
        const rows = (page.rows as { source?: unknown; row?: Record<string, unknown> }[]).map(
          (entry) => {
            if (entry?.source !== 'native') return entry;
            if (String(entry?.row?.id ?? '') !== String(orderId)) return entry;
            hit = true;
            return { ...entry, row: { ...entry.row, status } };
          },
        );
        return hit ? { ...page, rows } : data;
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });
      queryClient.invalidateQueries({ queryKey: ORDERS_PAGE_KEY });
      queryClient.invalidateQueries({ queryKey: ORDERS_FACETS_KEY });
    },
  });
}

export function useOrderStatuses(enabled = true) {
  return useQuery<OrderStatus[]>({
    queryKey: ['order-statuses'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: () => loadOrderStatuses(),
  });
}

export function useOnlineRiders(enabled: boolean) {
  return useQuery({
    queryKey: ['online-riders'],
    queryFn: async () => {
      const { TeamService } = await import('@/lib/api/services/teamService');
      const res = await TeamService.getTeamMembersAllPages({ role: 'RIDER', rider_available: true });
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, 'Failed to load online riders');
      }
      return (res.data ?? []).map((m) => ({
        id: m.id,
        name: m.name || 'Rider',
        phone: m.phone || '',
      }));
    },
    enabled,
    staleTime: 15_000,
  });
}

export const ORDERS_MAX_PAGE_SIZE = 100;

export const ORDERS_MAX_PAGES = 25;

export interface OrdersPage {
  orders: Order[];
  total: number;
  page: number;
  limit: number;
}

async function fetchOrdersPage(
  workspaceScopeId: string,
  filters?: {
    search?: string;
    status?: string;
    source?: string;
    page?: number;
    limit?: number;
  },
): Promise<OrdersPage> {
  const params = new URLSearchParams({
    workspace_id: workspaceScopeId,
    ...(filters?.search && { search: filters.search }),
    ...(filters?.status && { status: filters.status }),
    ...(filters?.source && { source: filters.source }),
    page: (filters?.page || 1).toString(),
    limit: (filters?.limit || 20).toString(),
  });

  const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/orders?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch orders');
  }
  const data = await response.json();
  const rows: Order[] = Array.isArray(data.data) ? data.data : [];
  const totalFromPagination =
    typeof data?.pagination?.total === 'number' ? data.pagination.total :
      (typeof data?.total === 'number' ? data.total : null);
  return {
    orders: rows,
    total: totalFromPagination ?? rows.length,
    page: data.page || 1,
    limit: data.limit || 20,
  };
}

export function useOrders(
  filters?: {
    search?: string;
    status?: string;
    source?: string;
    page?: number;
    limit?: number;
  },
  options?: { enabled?: boolean },
) {
  const workspaceScopeId = useWorkspaceScopeId();

  return useQuery<OrdersPage>({
    queryKey: ['orders', workspaceScopeId, filters],
    queryFn: async () => {
      if (!workspaceScopeId) return { orders: [], total: 0, page: 1, limit: 20 };
      return fetchOrdersPage(workspaceScopeId, filters);
    },
    enabled: !!workspaceScopeId && options?.enabled !== false,
    staleTime: 0,
    refetchInterval: 15000, // Poll every 15s so rider accept/status changes reflect without manual refresh
  });
}

export interface AllOrders {
  orders: Order[];
  total: number;
  truncated: boolean;
}

export function useAllOrders(options?: { enabled?: boolean }) {
  const workspaceScopeId = useWorkspaceScopeId();

  return useQuery<AllOrders>({
    queryKey: ['orders', workspaceScopeId, 'all'],
    queryFn: async () => {
      if (!workspaceScopeId) return { orders: [], total: 0, truncated: false };

      const first = await fetchOrdersPage(workspaceScopeId, {
        page: 1,
        limit: ORDERS_MAX_PAGE_SIZE,
      });

      const collected = [...first.orders];
      const total = Math.max(first.total, collected.length);

      let page = 1;
      let exhausted = first.orders.length < ORDERS_MAX_PAGE_SIZE;
      while (!exhausted && collected.length < total && page < ORDERS_MAX_PAGES) {
        page += 1;
        const next = await fetchOrdersPage(workspaceScopeId, {
          page,
          limit: ORDERS_MAX_PAGE_SIZE,
        });
        if (next.orders.length === 0) break;
        collected.push(...next.orders);
        exhausted = next.orders.length < ORDERS_MAX_PAGE_SIZE;
      }

      const seen = new Set<string>();
      const orders = collected.filter((o) => {
        const id = String((o as { id?: unknown }).id ?? '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      return {
        orders,
        total: Math.max(total, orders.length),
        truncated: !exhausted && orders.length < total,
      };
    },
    enabled: !!workspaceScopeId && options?.enabled !== false,
    staleTime: 60_000,
    refetchInterval: 180_000,
  });
}


export function useAvailableCatalogs(options?: { sync?: boolean }) {
  const workspaceScopeId = useWorkspaceScopeId();

  return useQuery<AvailableCatalog[]>({
    queryKey: ['available-catalogs', workspaceScopeId, options?.sync],
    queryFn: async () => {
      if (!workspaceScopeId) return [];

      const params = new URLSearchParams({ businessAccountId: workspaceScopeId });
      if (options?.sync) {
        params.append('sync', 'true');
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalogs?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) return [];
        throw new Error('Failed to fetch available catalogs');
      }
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!workspaceScopeId,
    staleTime: 0, // Disable caching
  });
}

export function useCatalogInfo() {
  const workspaceScopeId = useWorkspaceScopeId();

  return useQuery<CatalogInfo | null>({
    queryKey: ['catalog-info', workspaceScopeId],
    queryFn: async () => {
      if (!workspaceScopeId) return null;


      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/info?businessAccountId=${workspaceScopeId}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch catalog info');
      }
      const data = await response.json();
      return data.data || null; // Extract data from response
    },
    enabled: !!workspaceScopeId,
    staleTime: 0, // Disable caching
  });
}

export interface ProductsPageFilters {
  search?: string;
  category?: string;
  status?: string;
  page?: number;
  limit?: number;
  catalogId?: string;
}

async function fetchProductsPage(
  workspaceScopeId: string,
  filters?: ProductsPageFilters,
): Promise<{ products: Product[]; total: number; page: number; limit: number }> {
  const params = new URLSearchParams({
    workspace_id: workspaceScopeId,
    ...(filters?.search && { search: filters.search }),
    ...(filters?.category && { category: filters.category }),
    ...(filters?.status && { status: filters.status }),
    ...(filters?.catalogId && { catalogId: filters.catalogId }), // Pass catalogId
    page: (filters?.page || 1).toString(),
    limit: (filters?.limit || 20).toString(),
  });

  const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch products');
  }
  const data = await response.json();
  return data.data || { products: [], total: 0, page: 1, limit: 20 };
}

export const PRODUCTS_MAX_PAGE_SIZE = 250;

export const PRODUCTS_MAX_PAGES = 10;

export function useAllCatalogProducts(
  filters: Pick<ProductsPageFilters, 'catalogId'>,
  options?: { enabled?: boolean },
) {
  const workspaceScopeId = useWorkspaceScopeId();
  const catalogId = filters.catalogId;

  return useQuery<{ products: Product[]; total: number; truncated: boolean }>({
    queryKey: ['products', workspaceScopeId, 'all', catalogId],
    queryFn: async () => {
      if (!workspaceScopeId) return { products: [], total: 0, truncated: false };

      const first = await fetchProductsPage(workspaceScopeId, {
        catalogId,
        page: 1,
        limit: PRODUCTS_MAX_PAGE_SIZE,
      });

      const collected = [...first.products];
      const total = Math.max(first.total, collected.length);

      let page = 1;
      let exhausted = first.products.length < PRODUCTS_MAX_PAGE_SIZE;
      while (!exhausted && collected.length < total && page < PRODUCTS_MAX_PAGES) {
        page += 1;
        const next = await fetchProductsPage(workspaceScopeId, {
          catalogId,
          page,
          limit: PRODUCTS_MAX_PAGE_SIZE,
        });
        if (next.products.length === 0) break;
        collected.push(...next.products);
        exhausted = next.products.length < PRODUCTS_MAX_PAGE_SIZE;
      }

      const seen = new Set<string>();
      const products = collected.filter((p) => {
        const id = String((p as { id?: unknown }).id ?? '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      return {
        products,
        total: Math.max(total, products.length),
        truncated: !exhausted && products.length < total,
      };
    },
    enabled: !!workspaceScopeId && !!catalogId && options?.enabled !== false,
    staleTime: 60 * 1000,
  });
}

export function useProducts(filters?: ProductsPageFilters & {
  enabled?: boolean; // Caller opt-out (e.g. when the WhatsApp-catalog tab isn't active)
}) {
  const workspaceScopeId = useWorkspaceScopeId();

  return useQuery<{
    products: Product[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['products', workspaceScopeId, filters],
    queryFn: async () => {
      if (!workspaceScopeId) return { products: [], total: 0, page: 1, limit: 20 };
      return fetchProductsPage(workspaceScopeId, filters);
    },
    enabled: !!workspaceScopeId && filters?.enabled !== false,
    staleTime: 0, // Disable caching
  });
}

export function useInfiniteProducts(filters?: {
  search?: string;
  category?: string;
  status?: string;
  limit?: number;
  catalogId?: string;
  /** Only Meta-approved (WhatsApp) active products — what a product message can actually send. */
  approvedOnly?: boolean;
  enabled?: boolean;
}) {
  const workspaceScopeId = useWorkspaceScopeId();
  const limit = filters?.limit || 20;

  return useInfiniteQuery<{
    products: Product[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['products-infinite', workspaceScopeId, { ...filters, limit }],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      if (!workspaceScopeId) return { products: [], total: 0, page: 1, limit };

      const params = new URLSearchParams({
        workspace_id: workspaceScopeId,
        ...(filters?.search && { search: filters.search }),
        ...(filters?.category && { category: filters.category }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.catalogId && { catalogId: filters.catalogId }),
        ...(filters?.approvedOnly && { approved_only: 'true' }),
        page: String(pageParam),
        limit: String(limit),
      });

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch products');
      }
      const data = await response.json();
      return data.data || { products: [], total: 0, page: 1, limit };
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.products?.length) return undefined;
      const loaded = allPages.reduce((n, p) => n + (p?.products?.length || 0), 0);
      return loaded < (lastPage.total || 0) ? allPages.length + 1 : undefined;
    },
    enabled: !!workspaceScopeId && filters?.enabled !== false,
    staleTime: 0,
  });
}

export function useCommerceStats(options?: { from?: Date; to?: Date }) {
  const workspaceScopeId = useWorkspaceScopeId();

  return useQuery<CommerceStats>({
    queryKey: ['commerce-stats', workspaceScopeId, options?.from, options?.to],
    queryFn: async () => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const params = new URLSearchParams({ businessAccountId: workspaceScopeId });
      if (options?.from) {
        const d = options.from;
        params.append('startDate', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }
      if (options?.to) {
        const d = options.to;
        params.append('endDate', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/stats?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch commerce stats');
      }
      const data = await response.json();
      return data.data || {
        total_revenue: 0,
        total_orders: 0,
        total_products: 0,
        avg_order_value: 0,
        revenue_change: 0,
        products_change: 0,
        aov_change: 0,
        top_products: [],
        sales_history: []
      };
    },
    enabled: !!workspaceScopeId,
    staleTime: 0, // Disable caching
  });
}

const CATALOG_IMPORT_SETTLE_DELAYS_MS = [4_000, 12_000, 30_000] as const;

export function useSetupCatalog() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (catalogId: string) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          catalogId,
          businessAccountId: workspaceScopeId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to setup catalog');
      }

      return response.json();
    },
    onMutate: async (catalogId) => {
      await queryClient.cancelQueries({ queryKey: ['catalog-info', workspaceScopeId] });
      await queryClient.cancelQueries({ queryKey: ['available-catalogs', workspaceScopeId] });

      const previousCatalogInfo = queryClient.getQueryData(['catalog-info', workspaceScopeId]);
      const availableCatalogs = queryClient.getQueryData<AvailableCatalog[]>(['available-catalogs', workspaceScopeId]);

      if (availableCatalogs) {
        const selectedCatalog = availableCatalogs.find(cat => cat.id === catalogId);
        if (selectedCatalog) {
          queryClient.setQueryData(['catalog-info', workspaceScopeId], {
            id: catalogId,
            name: selectedCatalog.name,
            business_account_id: workspaceScopeId,
            whatsapp_catalog_id: catalogId,
            status: 'connected',
            product_count: selectedCatalog.product_count,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      }

      return { previousCatalogInfo };
    },
    onError: (err, catalogId, context) => {
      if (context?.previousCatalogInfo) {
        queryClient.setQueryData(['catalog-info', workspaceScopeId], context.previousCatalogInfo);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-info'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });
      queryClient.invalidateQueries({ queryKey: ['available-catalogs'] });
    },
    onSuccess: (data) => {
      const status = (data as { productImport?: { status?: string } } | undefined)
        ?.productImport?.status;
      if (status !== 'queued' && status !== 'already_running') return;

      for (const delay of CATALOG_IMPORT_SETTLE_DELAYS_MS) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['products'] });
          queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });
        }, delay);
      }
    },
  });
}



export function useCreateCatalog() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (name: string) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalogs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: workspaceScopeId,
          name,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create catalog');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', workspaceScopeId] });
    },
  });
}

export function useSyncCatalog() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (catalogId?: string) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: workspaceScopeId,
          catalogId, // Pass catalogId if provided
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to sync catalog');
      }

      const data = await response.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['catalog-info'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });

      return data;
    },
  });
}

export function useSyncProductFromMeta() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (metaProductId: string) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/commerce/products/${encodeURIComponent(metaProductId)}/sync`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ businessAccountId: workspaceScopeId }),
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult(
          { ...error, statusCode: response.status },
          'Failed to sync this product from Meta',
        );
      }

      return (await response.json()) as {
        success: boolean;
        found: boolean;
        created?: boolean;
        updated?: boolean;
        reviewState?: CatalogPublishState | null;
        message?: string;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useRelinkCatalogToWaba() {
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async () => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/relink-waba`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ businessAccountId: workspaceScopeId }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to re-link catalog to WhatsApp');
      }

      return response.json();
    },
  });
}

export function useDeleteCatalog() {
  const workspaceScopeId = useWorkspaceScopeId();
  const commerceScopeId = workspaceScopeId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (catalogId?: string) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      let url = `${API_CONFIG.BASE_URL}/commerce/catalog?businessAccountId=${workspaceScopeId}`;
      if (catalogId) {
        url += `&catalogId=${catalogId}`;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to delete catalog');
      }

      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['catalog-info', workspaceScopeId],
      });

      queryClient.invalidateQueries({ queryKey: ['orders', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['products', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', workspaceScopeId] });

      queryClient.invalidateQueries({
        queryKey: ['commerce-v2', 'store-connections', commerceScopeId],
      });
      queryClient.invalidateQueries({ queryKey: ['commerce-v2'] });
    },
  });
}

export function useRenameCatalog() {
  const workspaceScopeId = useWorkspaceScopeId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ catalogId, name }: { catalogId: string; name: string }) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/${catalogId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: workspaceScopeId,
          name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to rename catalog');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', workspaceScopeId] });
    },
  });
}

export function useProductSets(catalogId?: string) {
  const workspaceScopeId = useWorkspaceScopeId();

  return useQuery<{ id: string; name: string; product_count: number }[]>({
    queryKey: ['product-sets', workspaceScopeId, catalogId],
    queryFn: async () => {
      if (!workspaceScopeId) return [];

      const params = new URLSearchParams({ businessAccountId: workspaceScopeId });
      if (catalogId) params.append('catalogId', catalogId);

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) return [];
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch product sets');
      }
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!workspaceScopeId,
    staleTime: 0, // Disable caching
  });
}

export function useProductsBySet(setId?: string, opts?: { page?: number; limit?: number }) {
  const workspaceScopeId = useWorkspaceScopeId();
  const page = opts?.page ?? 1;
  const limit = opts?.limit ?? 20;

  return useQuery<{
    products: Product[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['products-by-set', workspaceScopeId, setId, page, limit],
    queryFn: async () => {
      if (!workspaceScopeId || !setId) return { products: [], total: 0, page, limit };
      const params = new URLSearchParams({
        businessAccountId: workspaceScopeId,
        page: String(page),
        limit: String(limit),
      });
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets/${setId}/products?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch products by set');
      }
      const data = await response.json();
      return data.data || { products: [], total: 0, page, limit };
    },
    enabled: !!workspaceScopeId && !!setId,
    staleTime: 0, // Disable caching
  });
}

export function useBulkImportProducts() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (data: {
      products: Array<{
        name: string;
        description: string;
        price: number;
        currency: string;
        category: string;
        availability: string;
        condition: string;
        brand?: string;
        url?: string;
        image_url: string | string[];
        retailer_id?: string;
      }>;
      catalogId?: string;
    }) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: workspaceScopeId,
          products: data.products,
          catalogId: data.catalogId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to bulk import products');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', workspaceScopeId] });
    },
  });
}

export function useCatalogBulkImportChunk() {
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (data: {
      products: Array<Record<string, unknown>>;
      catalogId?: string;
    }) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: workspaceScopeId,
          products: data.products,
          catalogId: data.catalogId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult(
          { ...error, statusCode: response.status },
          'Failed to import products',
        );
      }

      return response.json();
    },
  });
}

export function useInvalidateCommerceProducts() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['products', workspaceScopeId] });
    queryClient.invalidateQueries({ queryKey: ['catalog-info', workspaceScopeId] });
    queryClient.invalidateQueries({ queryKey: ['commerce-stats', workspaceScopeId] });
  }, [queryClient, workspaceScopeId]);
}

export function useAddProduct() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (productData: {
      name: string;
      description: string;
      price: number;
      currency: string;
      category: string;
      availability: string;
      condition: string;
      brand?: string;
      url?: string;
      retailer_id?: string;
      image_url?: string[];
      sets?: string[];
      newSetName?: string;
      catalogId?: string;
    }) => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          ...productData,
          businessAccountId: workspaceScopeId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create product');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', workspaceScopeId] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (params: { whatsappProductId: string; catalogId?: string }) => {
      if (!workspaceScopeId) throw new Error('No business account selected');
      let url = `${API_CONFIG.BASE_URL}/commerce/products/${encodeURIComponent(params.whatsappProductId)}?businessAccountId=${workspaceScopeId}`;
      if (params.catalogId) {
        url += `&catalogId=${encodeURIComponent(params.catalogId)}`;
      }
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to delete product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', workspaceScopeId] });
    },
  });
}

export function useBusinessProfile() {
  const workspaceScopeId = useWorkspaceScopeId();

  return useQuery<BusinessProfileSettings>({
    queryKey: ['business-profile-settings', workspaceScopeId],
    queryFn: async () => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/settings/profile?businessAccountId=${workspaceScopeId}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch business profile settings');
      }

      const data = await response.json();
      return data.data;
    },
    enabled: !!workspaceScopeId,
  });
}

export function useUpdateBusinessProfile() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (settings: {
      is_cart_enabled?: boolean;
      is_catalog_visible?: boolean;
    }): Promise<UpdateBusinessProfileResult> => {
      if (!workspaceScopeId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/settings/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: workspaceScopeId,
          settings,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update settings');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-profile-settings', workspaceScopeId] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();

  return useMutation({
    mutationFn: async (params: {
      whatsappProductId: string;
      updates: any;
      catalogId?: string;
    }) => {
      if (!workspaceScopeId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products/${params.whatsappProductId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ businessAccountId: workspaceScopeId, ...params.updates, catalogId: params.catalogId })
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', workspaceScopeId] });
      queryClient.invalidateQueries({ queryKey: ['product-sets', workspaceScopeId] });
    }
  });
}

export function useCreateProductSet() {
  const queryClient = useQueryClient();
  const workspaceScopeId = useWorkspaceScopeId();
  return useMutation({
    mutationFn: async (params: { name: string; catalogId?: string }) => {
      if (!workspaceScopeId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: workspaceScopeId,
          name: params.name,
          catalogId: params.catalogId
        })
      });
      if (!response.ok) throw new Error('Failed to create set');
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product-sets', workspaceScopeId, variables.catalogId] });
      queryClient.invalidateQueries({ queryKey: ['product-sets', workspaceScopeId] });
    },
  });
}
